import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { STUDIO } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useTypes = [
  "Photo / Video shoot",
  "Event",
  "Workshop",
  "Private gathering",
  "Presentation",
  "Other",
];

const fieldCls =
  "w-full bg-transparent border-0 border-b border-neutral-300 rounded-none py-3 font-body text-base placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 transition-colors";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    use_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.use_type) {
      toast.error("Please fill in your name, email and type of use.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Request sent. We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", date: "", use_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact"
      className="px-6 sm:px-10 py-24 sm:py-32 mx-auto max-w-[1600px]"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
        <div className="lg:col-span-5">
          <span className="font-body text-xs tracking-[0.2em] uppercase text-neutral-500">
            Bookings &amp; rentals
          </span>
          <h2 className="font-display font-medium tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-4">
            Let's plan your session.
          </h2>
          <p className="font-body text-lg text-neutral-700 mt-6 max-w-md">
            Tell us what you have in mind and your preferred date. We'll confirm
            availability and walk you through the space.
          </p>

          <div className="mt-10 space-y-1">
            <a
              href={`mailto:${STUDIO.email}`}
              data-testid="contact-email-link"
              className="nav-link font-display text-xl sm:text-2xl tracking-tight inline-block"
            >
              {STUDIO.email}
            </a>
            <div>
              <a
                href={STUDIO.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="nav-link font-body text-base text-neutral-700 inline-block mt-2"
              >
                {STUDIO.instagramHandle}
              </a>
            </div>
          </div>

          <div className="mt-10">
            <span className="block font-body text-xs tracking-[0.2em] uppercase text-neutral-500">
              Where to find us
            </span>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(STUDIO.mapQuery)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-address-link"
              className="nav-link font-body text-lg sm:text-xl font-medium inline-block mt-3"
            >
              {STUDIO.address}
            </a>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="booking-form"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-6 lg:col-start-7 space-y-8"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <input
              data-testid="input-name"
              className={fieldCls}
              placeholder="Full name *"
              value={form.name}
              onChange={update("name")}
            />
            <input
              data-testid="input-email"
              type="email"
              className={fieldCls}
              placeholder="Email *"
              value={form.email}
              onChange={update("email")}
            />
            <input
              data-testid="input-phone"
              className={fieldCls}
              placeholder="Phone"
              value={form.phone}
              onChange={update("phone")}
            />
            <input
              data-testid="input-date"
              type="text"
              onFocus={(e) => (e.target.type = "date")}
              onBlur={(e) => { if (!e.target.value) e.target.type = "text"; }}
              className={fieldCls}
              placeholder="Preferred date"
              value={form.date}
              onChange={update("date")}
            />
          </div>

          <div>
            <label className="font-body text-xs tracking-[0.2em] uppercase text-neutral-500">
              Type of use *
            </label>
            <div className="flex flex-wrap gap-2 mt-3">
              {useTypes.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`usetype-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setForm((f) => ({ ...f, use_type: t }))}
                  className={`px-4 py-2 border text-sm font-body transition-colors duration-200 ${
                    form.use_type === t
                      ? "bg-neutral-900 text-[#FAFAFA] border-neutral-900"
                      : "border-neutral-300 text-neutral-700 hover:border-neutral-900"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <textarea
            data-testid="input-message"
            rows={3}
            className={`${fieldCls} resize-none`}
            placeholder="Tell us about your project"
            value={form.message}
            onChange={update("message")}
          />

          <button
            type="submit"
            data-testid="submit-booking-btn"
            disabled={loading}
            className="w-full sm:w-auto px-10 py-4 bg-neutral-900 text-[#FAFAFA] font-body text-sm tracking-wide hover:bg-neutral-700 transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? "Sending…" : "Send request"}
          </button>
        </motion.form>
      </div>

      <div
        data-testid="map-embed"
        className="mt-16 sm:mt-20 w-full aspect-[16/8] sm:aspect-[21/7] overflow-hidden border border-neutral-200 grayscale hover:grayscale-0 transition-[filter] duration-700"
      >
        <iframe
          title="SO studio location"
          src={`https://maps.google.com/maps?q=${encodeURIComponent(STUDIO.mapQuery)}&z=16&output=embed`}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </section>
  );
};
